import React, { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Collapse,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { addDocumentWithNameDesc } from '../../services/ShareableApplicationService';
import {
  downloadDocument,
  deleteDocument,
} from '../../services/CustomerService';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Card from '@mui/material/Card';
import ImageIcon from '@mui/icons-material/Image';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import DeleteIcon from '@mui/icons-material/Delete';

const docTypeList = [
  'Panel serial numbers',
  'Inverter serial number',
  'Loto box',
  'Earthing protection device (ELCB / ELR)',
  'ACDB box',
  'DCDB box',
  'Lightening arrestor',
  'Earthing picture',
  'Fire extinguisher',
  'Plant photos',
  'Plant photo with customer',
];

const SitePhotosUploads = ({ documents, decodedId, onDocumentsUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertMsg, setAlertMsg] = useState('');
  const [alertIcon, setAlertIcon] = useState(null);
  const [alertOpenUnified, setAlertOpenUnified] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [url, setUrl] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);

  const handleOpenImageDialog = async (docId) => {
    try {
      setImageLoading(true);
      const data = await downloadDocument(docId);
      setUrl(data);
      setImageLoading(false);
    } catch (error) {
      setError('Failed to download image');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleFileUpload = async (event, docType) => {
    const file = event.target.files[0];
    if (!file) {
      setError('No file selected');
      setAlertOpen(true);
      return;
    }
    setLoading(true);
    try {
      const name = `${docType} at site`;
      const description = `uploaded on ${new Date().toLocaleString()}`;
      const dc = 'SITE PHOTOS';
      await addDocumentWithNameDesc(file, decodedId, dc, name, description);
      setSuccess(true);
      setError('');
      setAlertOpen(true);
      if (typeof onDocumentsUpdate === 'function') {
        onDocumentsUpdate();
      }
    } catch {
      setError('Upload failed.');
      setSuccess(false);
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url) => {
    setDownloadLoading(true);
    try {
      window.open(url, '_blank');
      setAlertType('success');
      setAlertMsg('Image download started!');
      setAlertIcon(<DownloadIcon fontSize='inherit' sx={{ mr: 1 }} />);
      setAlertOpenUnified(true);
    } catch (e) {
      setAlertType('error');
      setAlertMsg('Failed to download image');
      setAlertIcon(<DownloadIcon fontSize='inherit' sx={{ mr: 1 }} />);
      setAlertOpenUnified(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  const sitePhotoDocs = documents
    .filter((doc) => doc.document?.documentType === 'SITE PHOTOS')
    .map((doc, index) => ({
      id: doc.document?.id,
      name: doc.document?.name || `photo_${index}`,
    }));
  const handleAllDownload = async () => {
    setDownloadLoading(true);
    const zip = new JSZip();
    try {
      await Promise.all(
        sitePhotoDocs.map(async (doc, index) => {
          try {
            const url = await downloadDocument(doc.id);
            const response = await fetch(url);
            if (!response.ok)
              throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            const safeName = `${(doc.name || `photo_${doc.id}`).replace(/[^a-zA-Z0-9._-]/g, '_')}_${index}`;
            zip.file(safeName, blob);
          } catch (error) {}
        }),
      );
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'site-photos.zip');
      setAlertType('success');
      setAlertMsg('All photos downloaded!');
      setAlertIcon(<DownloadIcon fontSize='inherit' sx={{ mr: 1 }} />);
      setAlertOpenUnified(true);
    } catch (err) {
      setAlertType('error');
      setAlertMsg('Failed to download all images');
      setAlertIcon(<DownloadIcon fontSize='inherit' sx={{ mr: 1 }} />);
      setAlertOpenUnified(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteDocument(docId);
      if (typeof onDocumentsUpdate === 'function') {
        onDocumentsUpdate();
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
    setDeleteConfirmOpen(false);
    setDeleteDocId(null);
  };

  const handleDeleteClick = (docId) => {
    setDeleteDocId(docId);
    setDeleteConfirmOpen(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Collapse in={alertOpen}></Collapse>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={2}
        >
          <Typography variant='h5' fontWeight={600}>
            Site photos
          </Typography>
        </Stack>
        {sitePhotoDocs.length > 0 && (
          <Button
            sx={{ textTransform: 'none' }}
            variant='contained'
            size='small'
            onClick={handleAllDownload}
            disabled={downloadLoading}
          >
            {downloadLoading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            Download all photos
            <DownloadIcon sx={{ ml: 1 }} />
          </Button>
        )}
      </Stack>
      {docTypeList.map((docType) => (
        <Box key={docType} sx={{ mb: 4 }}>
          <Stack flexDirection={'row'} alignItems={'center'}>
            <Typography variant='h5' fontWeight={600} mb={2}>
              {docType}
            </Typography>

            <input
              accept='image/*'
              type='file'
              multiple
              id={`upload-${docType}`}
              style={{ display: 'none' }}
              onChange={(e) => handleFileUpload(e, docType)}
            />
            <label htmlFor={`upload-${docType}`}>
              <IconButton component='span' sx={{ mb: 2 }}>
                <ControlPointIcon />
              </IconButton>
            </label>
          </Stack>

          <Typography variant='subtitle2' mb={1}></Typography>
          <Grid container spacing={4}>
            {documents
              .filter(
                (doc) =>
                  doc.document?.url &&
                  doc.document?.name &&
                  doc.document?.name.startsWith(docType),
              )
              .map((doc, idx) => (
                <Grid item key={`doc-${idx}`}>
                  <Card
                    variant='outlined'
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      width: 200,
                    }}
                  >
                    <Stack direction='row' justifyContent='flex-end' height={0}>
                      <IconButton
                        sx={{ position: 'relative', top: 2 }}
                        color='error'
                        onClick={() => handleDeleteClick(doc?.document?.id)}
                      >
                        <DeleteIcon fontSize='medium' />
                      </IconButton>
                    </Stack>
                    <Box
                      cursor='grab'
                      onClick={() => handleOpenImageDialog(doc?.document?.id)}
                      sx={{
                        width: 90,
                        height: 90,
                        border: '2px dashed gray',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1,
                      }}
                    >
                      <ImageIcon fontSize='large' color='disabled' />
                    </Box>

                    <Typography variant='body1' fontWeight='bold'>
                      {(() => {
                        const desc = doc.document?.description || '';
                        const match = desc.match(
                          /uploaded on (\d{2})\/(\d{2})\/(\d{4})/,
                        );
                        if (match) {
                          const [, day, month, year] = match;
                          const dateObj = new Date(`${year}-${month}-${day}`);
                          return (
                            dateObj.toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }) +
                            `, ${dateObj.getHours()}:${dateObj.getMinutes()}`
                          );
                        }
                        return '';
                      })()}
                    </Typography>
                    <Button
                      onClick={() => handleOpenImageDialog(doc?.document?.id)}
                      startIcon={<VisibilityIcon />}
                      size='small'
                      sx={{ mt: 1, textTransform: 'none' }}
                      disabled={imageLoading}
                    >
                      {imageLoading ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : null}
                      Click to view image
                    </Button>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      ))}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogContent>
          <Typography>Are you sure, you want to delete this photo?</Typography>
        </DialogContent>
        <Stack
          direction='row'
          spacing={2}
          justifyContent='flex-end'
          sx={{ p: 2, pt: 0 }}
        >
          <Button onClick={() => setDeleteConfirmOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteDocId)}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        </Stack>
      </Dialog>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          <IconButton
            onClick={() => handleDownload(url)}
            sx={{ color: 'primary.main', background: 'white', mr: 1 }}
            aria-label='Download'
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: 'primary.main', background: 'white', mr: 1 }}
            aria-label='Close'
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent sx={{ p: 4 }}>
          <Box
            width={'100%'}
            height={'100%'}
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            {imageLoading ? (
              <CircularProgress />
            ) : (
              <Box
                component='img'
                src={url}
                alt='Preview'
                sx={{
                  height: 'auto',
                  width: '70vh',
                  maxWidth: '80vw',
                  borderRadius: 2,
                  cursor: 'grab',
                }}
                onClick={() => handleDownload(url)}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {success && (
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity='success' onClose={() => setSuccess(false)}>
            Document uploaded successfully!
          </Alert>
        </Snackbar>
      )}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert severity='error' onClose={() => setError('')}>
            {error}
          </Alert>
        </Snackbar>
      )}

      <Snackbar
        open={alertOpenUnified}
        autoHideDuration={4000}
        onClose={() => setAlertOpenUnified(false)}
      >
        <Alert
          severity={alertType}
          icon={alertIcon}
          onClose={() => setAlertOpenUnified(false)}
        >
          {alertMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SitePhotosUploads;
